/* What identifies the row a queued write is about.
 *
 * Extracted from sync.js for the same reason turns.js and session.js were: it
 * is pure logic that decides something load-bearing, and sync.js cannot be
 * imported without a Supabase config — it throws at module scope when there
 * isn't one — so nothing in there can be tested at all.
 *
 * That is not a hypothetical tidiness argument. The tenancy migration put
 * `tenant_id` at the head of every conflict target, which quietly changed the
 * shape of the string `pendingKeys` answers with, and the one caller went on
 * asking with the old shape. The lookup stopped matching, the protection it
 * guards was off, and every test still passed. Hence this file.
 *
 * Two questions, and they are NOT the same question:
 *
 *   opKey            is this the same row as that one, so a later write can
 *                    replace an earlier one still in the queue? Includes
 *                    tenant_id, because it is part of the conflict target the
 *                    database will actually upsert on.
 *   pendingKeysFrom  which rows of this table has the server not been told
 *                    about yet? Excludes tenant_id — see below.
 */

// Which columns say what row an upsert is about. The conflict target is the
// truth where there is one; otherwise it is the primary key, `id`.
export function opCols(op) {
  return (op.onConflict || 'id').split(',').map(function (c) { return c.trim(); });
}

// Their values off the row, or null if any of them is absent — an op that
// cannot say which row it is about cannot be matched against another one.
export function opValues(op, cols) {
  var parts = [];
  for (var i = 0; i < cols.length; i++) {
    var v = op.row[cols[i]];
    if (v == null) return null;
    parts.push(String(v));
  }
  return parts;
}

// What identifies the row an upsert is about, so a later write of the same row
// can replace an earlier one still waiting.
export function opKey(op) {
  if (op.type !== 'upsert' || !op.row) return null;
  var parts = opValues(op, opCols(op));
  return parts ? op.table + '|' + parts.join('|') : null;
}

/* Which rows of `table` still have a write waiting to go up, as a set of key
 * strings. A reload from the server must leave these alone: the server has not
 * been told about them yet, so its copy is stale by definition and taking it
 * would revert the change under whoever just made it.
 *
 * tenant_id is deliberately NOT part of these keys. Every conflict target now
 * leads with it, so including it answered `<tenant>|theme` while prefs.hydrate
 * asks about `theme`, and the lookup silently never matched. tt_settings is the
 * one table store.js refetches WITHOUT waiting for the queue to drain —
 * precisely because it trusts this — so an unsent settings change was
 * overwritten by the server's stale row seconds after somebody made it.
 *
 * It carries no information here in any case. The queue is one salon's by
 * construction: its localStorage key is namespaced per tenant, and foreign ops
 * are dropped before a flush. Excluding it also means a write made before the
 * session resolved — with no stamp on it yet, waiting for the column default to
 * attribute it — is still reported as pending, rather than skipped for having a
 * null in the middle of its key.
 */
export function pendingKeysFrom(ops, table) {
  var out = {};
  (ops || []).forEach(function (op) {
    if (op.table !== table || op.type !== 'upsert' || !op.row) return;
    var cols = opCols(op).filter(function (c) { return c !== 'tenant_id'; });
    var parts = opValues(op, cols);
    if (parts) out[parts.join('|')] = true;
  });
  return out;
}

/* The options for an upsert. `ignoreDuplicates` is ON CONFLICT DO NOTHING, for a table a
 * station may add to but not update: Postgres wants the UPDATE privilege for DO UPDATE
 * whether or not there turns out to be a conflict, so an ordinary upsert there is refused
 * every time. */
export function upsertOptions(op) {
  if (!op.onConflict) return undefined;
  return op.ignoreDuplicates
    ? { onConflict: op.onConflict, ignoreDuplicates: true }
    : { onConflict: op.onConflict };
}

/* Can this op go up in one request with the op at the head of the queue? */
export function sameBatch(head, op) {
  return op.type === 'upsert' && op.table === head.table &&
    (op.onConflict || '') === (head.onConflict || '') &&
    !!op.ignoreDuplicates === !!head.ignoreDuplicates;
}
